import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Backdrop,
  CircularProgress,
  Avatar,
  Divider,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import DocuwareTemplateService from "../../services/DocuwareTemplateService";
import notify from "../../utils/notifier";


const useStyles = makeStyles(() => ({
  paper: {
    width: 640,
    maxWidth: "calc(100vw - 32px)",
    boxShadow:
      "0px 8px 24px rgba(16, 24, 40, 0.12), 0px 2px 8px rgba(16, 24, 40, 0.06)",
    overflow: "hidden",
  },
  header: {
    padding: "18px 20px 10px 20px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  leftHead: { display: "flex", alignItems: "center" },
  glyph: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "1px solid #E6F0F4",
    background: "#F2FAFD",
    color: "#0E4B5B",
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    marginRight: 10,
    fontSize: 14,
  },
  sub: { color: "#6B7280", marginTop: 2, fontSize: 12 },
  closeBtn: { color: "#94A3B8", padding: 6 },
  divider: { background: "#E6E7EB" },
  content: { padding: 0 },
  confirmSection: {
    background: "#F8FCFF",
    borderTop: "1px solid #CFE9F6",
    borderBottom: "1px solid #CFE9F6",
    padding: "18px 20px",
  },
  confirmHead: {
    fontWeight: 600,
    fontSize: 14,
    color: "#0E4B5B",
    marginBottom: 4,
  },
  confirmBody: {
    fontSize: 13,
    color: "#374151",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "16px 20px 20px",
  },
  cancel: {
    textTransform: "none",
    fontWeight: 500,
    color: "#344054",
    marginRight: 8,
  },
  delete: {
    textTransform: "none",
    fontWeight: 700,
    color: "#fff",
    background: "#0B3B53",
    borderRadius: 999,
    padding: "6px 18px",
    "&:hover": { background: "#083446" },
    "&:disabled": { opacity: 0.7, color: "#fff" },
  },
}));

export default function RecycleBinModal({
  open,
  onClose,
  document,
  fileIdentifiers = [],
  loading = false,
  type ='delete',
  count = 0,
  handleRefresh
}) {
  const classes = useStyles();

  const singleId = document?.id;

  if (!Array.isArray(fileIdentifiers)) {
    fileIdentifiers = Array.from(fileIdentifiers);
  }

  const bulk = fileIdentifiers.length > 0;
  let effectiveIds = bulk ? fileIdentifiers : singleId ? [singleId] : [];

  let title = bulk ? "Bulk Delete Files" : "Delete Selected File";

  if (type == 'delete'){
    title = bulk ? "Bulk Delete Files" : "Delete Selected File";
  }
  else if (type == 'permanentDelete'){
    title = bulk ? "Permanently Delete Selected Files" : "Permanently Delete Selected File";
  }
  else if (type == 'restore'){
    title = bulk ? "Bulk Restore Files" : "Restore Selected File";
  }
  else if (type == 'empty'){
    title = "Empty Recycle bin";
    effectiveIds = ['1'];
  }

  let subtitle = bulk
    ? `${fileIdentifiers.length} item${fileIdentifiers.length > 1 ? "s" : ""} selected`
    : (document?.name || document?.file_name || "1 item selected");

  if (type == 'empty'){
    subtitle = `${count} items selected`;
  }


  const onConfirm = (ids = []) => {

    if (!Array.isArray(ids)) {
        ids = Array.from(ids);
    }
      

    let requestObj = {};

    requestObj.documentIds = ids;

    if (type=='delete'){
        DocuwareTemplateService.softDelete(requestObj).then(response => {
            notify({
                type: "success",
                message: "Successfully moved the documents to recycle bin"
            });
            onClose();
            handleRefresh();

        }).catch(error => {
            notify({
                type: 'error',
                message: 'Error moving the documents to recycle bin'
            });
            onClose();

        });
    }
    else if (type=='restore'){
        DocuwareTemplateService.restore(requestObj).then(response => {
            notify({
                type: "success",
                message: "Successfully restored the documents"
            });
            onClose();
            handleRefresh();

        }).catch(error => {
            notify({
                type: 'error',
                message: 'Error restoring the documents'
            });
            onClose();

        });
    }
    else if (type=='permanentDelete'){
        DocuwareTemplateService.permanentDelete(requestObj).then(response => {
            notify({
                type: "success",
                message: "Successfully deleted the documents"
            });
            handleRefresh();
        onClose();

        }).catch(error => {
            notify({
                type: 'error',
                message: 'Error deleted the documents'
            });
            onClose();
            handleRefresh()

        });
    }
    else{
        DocuwareTemplateService.emptyRecycleBin().then(response => {
            notify({
                type: "success",
                message: "Successfully emptied the recycle bin"
            });
            onClose();
            handleRefresh();

        }).catch(error => {
            notify({
                type: 'error',
                message: 'Error while emptying the recycle bin'
            });
            onClose();

        });
    }        
  };

  return (
    <>
      <Backdrop open={loading} style={{ color: "#0E4B5B", zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.paper }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.leftHead}>

            <div className={classes.glyph}>
              <ReportProblemOutlinedIcon className={classes.icon} />
            </div>

            <Box>
              <Typography variant="h6">
              <b>{title}</b>
              </Typography>
              <Typography className={classes.sub}>{subtitle}</Typography>
            </Box>
           

          </Box>
          <IconButton className={classes.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider className={classes.divider} />

        {/* Confirm Section — fills modal width */}
        <DialogContent className={classes.content}>
          <Box className={classes.confirmSection}>
            <Typography className={classes.confirmHead}>
              {(type=='restore') ? 'Confirm Restoration' : 'Confirm Deletion'}
            </Typography>
            <Typography className={classes.confirmBody}>
              {(type=='restore') ? 'Are you sure you want to restore the selected files to their original location? This cannot be undone.' :  (type=='delete') ? 'Are you sure you want to move the selected files to recycle bin?' : 'The files will be deleted permanently. This action cannot be undone.'}
            </Typography>
          </Box>

          {/* Footer Actions */}
          <Box className={classes.actions}>
            <Button onClick={onClose} className={classes.cancel}>
              Cancel
            </Button>
            <Button
              className={classes.delete}
              variant="contained"
              disableElevation
              onClick={() => onConfirm(effectiveIds)}
              disabled={effectiveIds.length === 0}
            >
              {type=='restore' ? "Yes, Restore" : "Yes, Delete"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
